class OpeningLanding {
  constructor(root) {
    this.root = root;
    document.documentElement.classList.add('opening-landing-page');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.reveals = [...root.querySelectorAll('[data-reveal]')];
    this.slides = [...root.querySelectorAll('[data-slide]')];
    this.progress = root.querySelector('[data-chapter-progress]');
    this.progressLinks = [];
    this.activeSlideIndex = 0;
    this.scrollFrame = 0;
    this.touch = null;
    this.pagingState = 'idle';
    this.wheelDelta = 0;
    this.wheelIntentDelta = 0;
    this.wheelFrame = 0;
    this.wheelSamples = [];
    this.discountPromise = this.applyCampaignDiscount();
    this.guardProductLinks();
    this.setupReveal();
    this.setupProgress();
    if (!this.reduceMotion) {
      document.documentElement.classList.add('opening-custom-paging');
      this.setupPaging();
      this.setupMotion();
    }
  }

  async applyCampaignDiscount() {
    const code = this.root.dataset.campaignDiscountCode;
    if (!code) return false;
    const status = this.root.querySelector('[data-campaign-status]');
    try {
      if (window.YoriaCampaign) {
        await window.YoriaCampaign.ensure();
        this.root.classList.add('is-campaign-discount-ready');
        return true;
      }
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: code,
          attributes: { yoria_campaign_discount: code },
        }),
      });
      if (!response.ok) throw new Error(`Discount request failed with ${response.status}`);
      const cart = await response.json();
      const saved = cart.discount_codes?.some((discount) => discount.code?.toLowerCase() === code.toLowerCase());
      if (!saved) throw new Error('Discount code was not saved to the cart');
      try { localStorage.setItem('yoria-campaign-discount', code); } catch {}
      this.root.classList.add('is-campaign-discount-ready');
      return true;
    } catch (error) {
      this.root.classList.add('has-campaign-discount-error');
      if (status) {
        const english = document.documentElement.lang?.toLowerCase().startsWith('en');
        const heading = status.querySelector('strong');
        const detail = status.querySelector('.opening-editorial__coupon-copy > span');
        if (heading) heading.textContent = english ? 'Coupon could not be applied' : 'クーポンを適用できませんでした';
        if (detail) detail.textContent = english ? 'Please try reloading this page' : 'ページを再読み込みしてお試しください';
      }
      console.error(error);
      return false;
    }
  }

  guardProductLinks() {
    this.root.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.addEventListener('click', async (event) => {
        if (!this.discountPromise || this.root.classList.contains('is-campaign-discount-ready')) return;
        event.preventDefault();
        await this.discountPromise;
        window.location.assign(link.href);
      });
    });
  }

  setupReveal() {
    if (this.reduceMotion || !('IntersectionObserver' in window)) {
      this.reveals.forEach((element) => element.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    this.reveals.forEach((element) => observer.observe(element));
  }

  setupProgress() {
    if (!this.slides.length || !this.progress) return;
    this.slides.forEach((slide, index) => {
      const link = document.createElement('a');
      link.href = `#${slide.id}`;
      link.dataset.progressLink = '';
      link.setAttribute('aria-label', `${index + 1} / ${this.slides.length} ${slide.dataset.slideLabel || ''}`);
      link.innerHTML = '<span></span>';
      this.progress.append(link);
    });
    this.progressLinks = [...this.progress.querySelectorAll('[data-progress-link]')];
    if (!('IntersectionObserver' in window)) return;
    const linksById = new Map(this.progressLinks.map((link) => [link.getAttribute('href').slice(1), link]));
    const observer = new IntersectionObserver((entries) => {
      if (this.scrollFrame) return;
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      this.activateSlide(this.slides.indexOf(visible.target));
    }, { threshold: [0.2, 0.45, 0.7], rootMargin: '-15% 0px -35% 0px' });
    this.slides.forEach((slide) => observer.observe(slide));
    this.progressLinks.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        if (this.reduceMotion) return;
        event.preventDefault();
        this.goToSlide(index);
      });
    });
    this.activateSlide(0);
  }

  activateSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    this.activeSlideIndex = index;
    this.root.classList.toggle('is-cover-active', index === 0);
    this.slides.forEach((slide, slideIndex) => slide.classList.toggle('is-current-slide', slideIndex === index));
    this.progressLinks.forEach((link, linkIndex) => link.classList.toggle('is-current', linkIndex === index));
  }

  setupPaging() {
    this.root.querySelectorAll('a[href^="#"]').forEach((link) => {
      if (link.matches('[data-progress-link]')) return;
      const target = document.querySelector(link.getAttribute('href'));
      const slide = target?.matches('[data-slide]') ? target : target?.closest('[data-slide]');
      const index = slide ? this.slides.indexOf(slide) : -1;
      if (index < 0) return;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.goToSlide(index);
      });
    });

    window.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      event.preventDefault();
      this.observeWheel(event);
    }, { passive: false });

    window.addEventListener('keydown', (event) => {
      const direction = ['ArrowDown', 'PageDown', ' '].includes(event.key) ? 1 : ['ArrowUp', 'PageUp'].includes(event.key) ? -1 : 0;
      const activeTag = document.activeElement?.tagName || '';
      if (!direction || /INPUT|TEXTAREA|SELECT/.test(activeTag) || (event.key === ' ' && /A|BUTTON/.test(activeTag))) return;
      event.preventDefault();
      if (this.pagingState !== 'idle' || event.repeat) return;
      this.goToSlide(this.nearestSlideIndex() + direction);
    });

    window.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      if (this.pagingState !== 'idle') {
        this.touch = { blocked: true, vertical: true };
        return;
      }
      const point = event.touches[0];
      this.touch = { x: point.clientX, y: point.clientY, lastY: point.clientY, startScroll: window.scrollY, startIndex: this.nearestSlideIndex(), vertical: null };
    }, { passive: true });

    window.addEventListener('touchmove', (event) => {
      if (!this.touch || event.touches.length !== 1) return;
      if (this.touch.blocked) {
        event.preventDefault();
        return;
      }
      const point = event.touches[0];
      const dx = point.clientX - this.touch.x;
      const dy = point.clientY - this.touch.y;
      if (this.touch.vertical === null && Math.hypot(dx, dy) > 8) this.touch.vertical = Math.abs(dy) > Math.abs(dx);
      if (!this.touch.vertical) return;
      event.preventDefault();
      this.touch.lastY = point.clientY;
      window.scrollTo(0, this.touch.startScroll - dy);
    }, { passive: false });

    const finishTouch = (event) => {
      if (!this.touch) return;
      if (this.touch.blocked) {
        this.touch = null;
        return;
      }
      const distance = this.touch.y - this.touch.lastY;
      const direction = event.type !== 'touchcancel' && Math.abs(distance) > 42 ? Math.sign(distance) : 0;
      const targetIndex = direction ? this.touch.startIndex + direction : this.touch.startIndex;
      this.touch = null;
      this.goToSlide(targetIndex);
    };
    window.addEventListener('touchend', finishTouch, { passive: true });
    window.addEventListener('touchcancel', finishTouch, { passive: true });

    window.addEventListener('scrollend', () => {
      if (!this.scrollFrame && !this.touch) this.goToSlide(this.nearestSlideIndex());
    }, { passive: true });
  }

  observeWheel(event) {
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    const delta = event.deltaY * unit;
    if (this.wheelDelta && Math.sign(this.wheelDelta) !== Math.sign(delta)) this.wheelDelta = 0;
    this.wheelDelta += delta;
    if (this.wheelFrame) return;
    this.wheelFrame = window.requestAnimationFrame(() => {
      this.wheelFrame = 0;
      const sampleDelta = this.wheelDelta;
      this.wheelDelta = 0;
      this.processWheelSample(sampleDelta);
    });
  }

  processWheelSample(delta) {
    const magnitude = Math.abs(delta);
    if (magnitude < 4) return;
    const sample = { time: performance.now(), delta: magnitude, direction: Math.sign(delta) };
    const previous = this.wheelSamples.at(-1);
    const restarted = !previous || sample.time - previous.time > 180 || sample.direction !== previous.direction;
    if (restarted) {
      this.wheelSamples = [];
      this.wheelIntentDelta = 0;
    }
    this.wheelSamples.push(sample);
    if (this.wheelSamples.length > 40) this.wheelSamples.shift();

    if (this.pagingState !== 'idle') {
      this.wheelIntentDelta = 0;
      return;
    }
    this.wheelIntentDelta += delta;
    if (Math.abs(this.wheelIntentDelta) < 32) return;

    const shortSamples = this.wheelSamples.slice(-3);
    const shortAverage = shortSamples.reduce((sum, item) => sum + item.delta, 0) / shortSamples.length;
    const longAverage = this.wheelSamples.reduce((sum, item) => sum + item.delta, 0) / this.wheelSamples.length;
    const increased = !previous || sample.direction !== previous.direction || sample.delta > previous.delta + 2;
    const sustained = shortAverage >= longAverage * .98;
    if (!restarted && !increased && !sustained) return;

    const direction = Math.sign(this.wheelIntentDelta);
    this.wheelIntentDelta = 0;
    this.goToSlide(this.nearestSlideIndex() + direction);
  }

  nearestSlideIndex() {
    const viewportMiddle = window.scrollY + window.innerHeight / 2;
    let closest = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, index) => {
      const middle = slide.offsetTop + slide.offsetHeight / 2;
      const distance = Math.abs(middle - viewportMiddle);
      if (distance < closestDistance) {
        closest = index;
        closestDistance = distance;
      }
    });
    return closest;
  }

  goToSlide(index) {
    if (this.pagingState !== 'idle') return;
    const targetIndex = Math.max(0, Math.min(this.slides.length - 1, index));
    const target = this.slides[targetIndex].offsetTop;
    const start = window.scrollY;
    const distance = target - start;
    if (Math.abs(distance) < 1) {
      this.activateSlide(targetIndex);
      return;
    }
    this.pagingState = 'animating';
    this.activateSlide(targetIndex);
    const duration = Math.max(560, Math.min(760, 560 + Math.abs(distance) / window.innerHeight * 120));
    const startedAt = performance.now();
    const ease = this.cubicBezier(.4, .14, .3, 1);
    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      window.scrollTo(0, start + distance * ease(progress));
      if (progress < 1) this.scrollFrame = window.requestAnimationFrame(step);
      else {
        window.scrollTo(0, target);
        this.scrollFrame = 0;
        this.pagingState = 'idle';
      }
    };
    this.scrollFrame = window.requestAnimationFrame(step);
  }

  cubicBezier(x1, y1, x2, y2) {
    const sample = (a1, a2, t) => 3 * a1 * (1 - t) ** 2 * t + 3 * a2 * (1 - t) * t ** 2 + t ** 3;
    const slope = (a1, a2, t) => 3 * a1 * (1 - t) ** 2 + 6 * (a2 - a1) * (1 - t) * t + 3 * (1 - a2) * t ** 2;
    return (progress) => {
      let t = progress;
      for (let iteration = 0; iteration < 5; iteration += 1) {
        const difference = sample(x1, x2, t) - progress;
        const currentSlope = slope(x1, x2, t);
        if (Math.abs(currentSlope) < .001) break;
        t -= difference / currentSlope;
      }
      return sample(y1, y2, Math.max(0, Math.min(1, t)));
    };
  }

  setupMotion() {
    let ticking = false;
    const update = () => {
      const viewport = window.innerHeight;
      this.root.querySelectorAll('[data-parallax]').forEach((media) => {
        const rect = media.getBoundingClientRect();
        const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
        media.style.setProperty('--parallax-y', `${Math.max(-18, Math.min(18, progress * -18))}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }
}

document.querySelectorAll('[data-opening-landing]').forEach((root) => new OpeningLanding(root));
